import { createFileRoute } from '@tanstack/react-router';
import { useState } from 'react';
import {
  useMyMfaState,
  useRequestAdminEmailMfa,
  useVerifyAdminEmailMfa,
} from '@/data/hardening';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Label } from '@/components/ui/Label';
import { Badge } from '@/components/ui/Badge';
import { toast } from '@/lib/toast';

export const Route = createFileRoute('/admin/security')({
  component: AdminSecurity,
});

function AdminSecurity() {
  const state = useMyMfaState();
  const requestEmail = useRequestAdminEmailMfa();
  const verifyEmail = useVerifyAdminEmailMfa();

  const [sentTo, setSentTo] = useState<string | null>(null);
  const [expiresAt, setExpiresAt] = useState<string | null>(null);
  const [code, setCode] = useState('');

  async function sendCode() {
    try {
      const r = await requestEmail.mutateAsync();
      setSentTo(r.sent_to);
      setExpiresAt(r.expires_at);
      setCode('');
      toast({
        title: 'Verification code sent',
        description: `We emailed a 6-digit code to ${r.sent_to}.`,
        variant: 'success',
      });
    } catch (err) {
      toast({
        title: "Couldn't send code",
        description: err instanceof Error ? err.message : 'Unknown error',
        variant: 'error',
      });
    }
  }

  async function confirm() {
    try {
      const ok = await verifyEmail.mutateAsync(code.trim());
      if (ok) {
        toast({
          title: 'Email MFA verified',
          description: 'This admin session can now access protected operations.',
          variant: 'success',
        });
        setCode('');
        return;
      }
      toast({
        title: 'Verification failed',
        description: 'That code is incorrect or expired.',
        variant: 'error',
      });
    } catch (err) {
      toast({
        title: 'Verification failed',
        description: err instanceof Error ? err.message : 'Wrong code',
        variant: 'error',
      });
    }
  }

  const lastVerifiedAt = state.data?.last_email_mfa_at ?? state.data?.last_aal2_at ?? null;
  const verified =
    lastVerifiedAt != null && Date.now() - new Date(lastVerifiedAt).getTime() < 12 * 60 * 60 * 1000;

  return (
    <div className="space-y-10">
      <header>
        <p className="tracking-[var(--tracking-wide)] text-[var(--color-ink-muted)] text-[var(--text-sm)] uppercase">
          Operations
        </p>
        <h1 className="mt-2">Security</h1>
        <p className="mt-2 max-w-prose text-[var(--color-ink-muted)]">
          Admin role requires an email verification check. Audit log access and destructive
          operations enforce this at the database row level for each signed-in session.
        </p>
      </header>

      <section aria-labelledby="mfa-heading" className="surface-elevated space-y-4 p-6">
        <header className="flex items-baseline justify-between gap-3">
          <h2 id="mfa-heading" className="font-[var(--font-display)] text-[var(--text-xl)]">
            Email MFA check
          </h2>
          <Badge tone={verified ? 'success' : 'warn'}>
            {verified ? 'Verified' : 'Email check required'}
          </Badge>
        </header>

        {verified && (
          <p className="text-[var(--color-ink-muted)]">
            This admin session was verified on{' '}
            {lastVerifiedAt ? new Date(lastVerifiedAt).toLocaleString() : '—'}.
          </p>
        )}

        <p className="text-[var(--color-ink-muted)]">
          We'll email a 6-digit code to your signed-in admin email address. The code expires in 10
          minutes, and a successful check unlocks protected admin operations for this browser session.
        </p>

        <div className="flex flex-wrap items-center gap-3">
          <Button onClick={() => void sendCode()} disabled={requestEmail.isPending}>
            {requestEmail.isPending ? 'Sending…' : sentTo ? 'Send another code' : 'Email me a code'}
          </Button>
          {sentTo && (
            <span className="text-[var(--color-ink-muted)] text-[var(--text-sm)]">
              Sent to {sentTo}
              {expiresAt ? `; expires ${new Date(expiresAt).toLocaleTimeString()}` : ''}
            </span>
          )}
        </div>

        {sentTo && (
          <div>
            <Label htmlFor="mfa-code">6-digit code</Label>
            <Input
              id="mfa-code"
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
              placeholder="123 456"
              inputMode="numeric"
              autoComplete="one-time-code"
              className="max-w-xs font-mono tracking-widest"
              onKeyDown={(e) => {
                if (e.key === 'Enter' && code.length === 6) void confirm();
              }}
            />
            <Button
              className="mt-3"
              onClick={() => void confirm()}
              disabled={code.length !== 6 || verifyEmail.isPending}
            >
              {verifyEmail.isPending ? 'Verifying…' : 'Verify'}
            </Button>
          </div>
        )}
      </section>
    </div>
  );
}
